import { useContext, useState, useRef, useEffect, useMemo } from "react";
import { CenterContext } from "../../../Context";
import { ChevronDown, MapPin, Check, Building2 } from "lucide-react";
import { usePrimaryColor } from "./PrimaryColorContext";

/**
 * InlineLocationSelect Component
 * Premium inline location dropdown that blends into surrounding content.
 * Trigger renders as subtle text + chevron; dropdown floats with flat-premium styling.
 * Uses ${primaryColor} for dynamic theming.
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} props.value - Selected location ID
 * @param {Function} props.setValue - Callback to update selected location
 * @param {boolean} [props.all=false] - Include `All locations" option
 * @param {boolean} [props.disabled=false] - Disabled state
 * @param {string} [props.className] - Additional CSS classes
 * @param {string} [props.size="medium"] - Size variant ("small", "medium")
 * @param {string} [props.placeholder] - Placeholder text when no selection
 *
 * @author Forge
 * @date 2026-04-10
 * @version 1.0.0
 */
export default function InlineLocationSelect({
  value,
  setValue,
  all = false,
  disabled = false,
  className = "",
  size = "medium",
  placeholder = "Select location",
}) {
  const { center } = useContext(CenterContext);
  const { primaryColor } = usePrimaryColor();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const wrapperRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    setSearch("");
    const timer = setTimeout(() => searchRef.current?.focus(), 50);
    return () => clearTimeout(timer);
  }, [isOpen]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    function handleKey(e) {
      if (e.key === "Escape" && isOpen) setIsOpen(false);
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [isOpen]);

  const locations = useMemo(() => {
    if (!center) return [];
    return center.locations.filter((l) => {
      if (all) return true;
      return l.name !== "All locations";
    });
  }, [center, all]);

  const selectedLocation = locations.find((l) => l._id === value);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return locations;
    return locations.filter((l) => l.name.toLowerCase().includes(q));
  }, [locations, search]);

  if (!center) return null;

  const handleSelect = (id) => {
    setValue(id);
    setIsOpen(false);
  };

  const isSmall = size === "small";

  return (
    <div className={`relative ${className}`} ref={wrapperRef}>
      {/* Inline Trigger */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`
          inline-flex items-center gap-1.5 rounded-md transition-all duration-150 group
          ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
          ${
            isOpen
              ? `bg-${primaryColor}-50 text-${primaryColor}-800`
              : `hover:bg-slate-50 text-slate-800 hover:text-${primaryColor}-800`
          }
          ${isSmall ? "py-0.5 px-1 -ml-1" : "py-1 px-1.5 -ml-1.5"}
        `}
      >
        {selectedLocation ? (
          <span
            className={`font-bold truncate max-w-[180px] ${
              isSmall ? "text-sm" : "text-[0.9rem]"
            }`}
          >
            {selectedLocation.name}
          </span>
        ) : (
          <span
            className={`italic truncate max-w-[180px] ${
              isSmall ? "text-xs" : "text-sm"
            } text-slate-400`}
          >
            {placeholder}
          </span>
        )}
        <ChevronDown
          size={isSmall ? 12 : 14}
          strokeWidth={2.5}
          className={`flex-shrink-0 text-slate-400 transition-transform duration-150 ${
            isOpen ? `rotate-180 text-${primaryColor}-800` : ""
          }`}
        />
      </button>

      {/* Floating Dropdown */}
      {isOpen && !disabled && (
        <div
          className="absolute top-full left-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-[0_8px_30px_rgba(0,0,0,0.08)] z-50 overflow-hidden"
          style={{
            animation: "inlineLocDropIn 150ms cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          {/* Search */}
          {locations.length > 5 && (
            <div className="p-2 border-b border-slate-100">
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search locations..."
                className={`w-full bg-slate-50 border border-slate-200 rounded-lg text-slate-800 text-[0.8rem] placeholder-slate-400 focus:outline-none focus:border-${primaryColor}-800 focus:ring-2 focus:ring-[rgba(0,0,0,0.03)] h-8 px-3 transition-all duration-150`}
              />
            </div>
          )}

          {/* Items */}
          <div className="max-h-[260px] overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="py-6 flex flex-col items-center gap-2">
                <Building2 size={24} strokeWidth={1.5} className="text-slate-300" />
                <span className="text-[0.8rem] text-slate-400 italic">
                  {search ? "No results found" : "No locations available"}
                </span>
              </div>
            ) : (
              filtered.map((location) => {
                const isSelected = value === location._id;
                return (
                  <div
                    key={location._id}
                    onClick={() => handleSelect(location._id)}
                    className={`px-3.5 py-2.5 cursor-pointer transition-colors duration-100 border-l-[3px] flex items-center justify-between gap-3 ${
                      isSelected
                        ? `bg-${primaryColor}-50 border-l-[${primaryColor}]`
                        : "border-l-transparent hover:bg-[#f8fafc] hover:border-l-slate-300"
                    }`}
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <MapPin
                        size={13}
                        strokeWidth={2}
                        className={`flex-shrink-0 ${
                          isSelected
                            ? `text-${primaryColor}-800`
                            : "text-slate-400"
                        }`}
                      />
                      <span
                        className={`text-[0.85rem] truncate ${
                          isSelected
                            ? "font-bold text-slate-900"
                            : "font-semibold text-slate-700"
                        }`}
                      >
                        {location.name}
                      </span>
                    </div>
                    {isSelected && (
                      <div className={`flex-shrink-0 w-5 h-5 rounded-full bg-${primaryColor}-800 flex items-center justify-center text-white`}>
                        <Check size={11} strokeWidth={3} />
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Footer count */}
          {filtered.length > 0 && (
            <div className="px-3.5 py-1.5 border-t border-slate-100 bg-slate-50/50">
              <span className="text-[0.6rem] text-slate-400 font-medium">
                {filtered.length} location{filtered.length !== 1 ? "s" : ""}
                {search ? ` matching "${search}"` : ""}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Inline keyframes */}
      <style>{`
        @keyframes inlineLocDropIn {
          from {
            opacity: 0;
            transform: translateY(-6px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}
